import { Injectable, PLATFORM_ID, inject, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { AudioRingBuffer } from '../utils/audio-ring-buffer';
import { AudioTimeline, PRE_ROLL_MS } from '../utils/audio-timeline';
import { VadDetector, VAD_CONFIG_PADRAO, VadEvento } from '../utils/vad-detector';
import { float32ToWav16k } from '../utils/audio-wav.util';

export type CaptureStatus =
  | 'desligado'
  | 'sem-suporte'
  | 'permissao-negada'
  | 'erro'
  | 'ativo';

/**
 * Quem reservou o microfone. O serviço é COMPARTILHADO: o modo conversa (VAD contínuo) e o
 * push-to-talk manual usam o mesmo stream, o mesmo AudioContext e o mesmo ring buffer.
 *
 * Mecanismo escolhido: **contagem de reservas por dono nomeado** (em vez de um contador anônimo).
 * `abrir(dono)` reserva, `fechar(dono)` devolve; o grafo só é derrubado quando a última reserva
 * sai. É explícito de raciocinar — dá para perguntar "quem está com o microfone?" e responder —
 * e impede o bug de "quem fechou por último derruba o que o outro está usando": um clique no
 * push-to-talk não pode matar o modo conversa, nem vice-versa.
 */
export type CapturaDono = 'conversa' | 'manual';

const STORAGE_KEY = 'voice_device_id';
/** Segundos de áudio mantidos no ring buffer — folga larga sobre o pre-roll de 300 ms. */
const BUFFER_SEGUNDOS = 20;
/** Passo do laço de decisão. Mais grosso que o quadro de áudio de propósito: o pre-roll absorve. */
const PASSO_DECISAO_MS = 20;
/** Taxa assumida antes de existir um AudioContext (só serve para o buffer-placebo inicial). */
const TAXA_PADRAO = 16000;

/**
 * Dono ÚNICO do microfone no One.UI.
 *
 * Dois ramos correm em paralelo a partir do mesmo stream:
 *  - DECISÃO: bandpass 300-3400 Hz → Analyser → RMS → VadDetector. O filtro serve só para julgar.
 *  - GRAVAÇÃO: AudioWorklet → ring buffer de PCM cru, SEM filtro — o Gemini recebe o áudio íntegro.
 *
 * Como o buffer é escrito continuamente, quando o VAD conclui que houve fala os 300 ms anteriores
 * ao disparo já estão gravados. É por isso que o início da frase deixa de ser cortado.
 *
 * O tempo usado nas decisões e na extração vem da {@link AudioTimeline} — a posição do ring
 * buffer, NUNCA `AudioContext.currentTime` (ver o comentário da classe para o porquê).
 */
@Injectable({ providedIn: 'root' })
export class AudioCaptureService {
  private readonly platformId = inject(PLATFORM_ID);

  private stream: MediaStream | null = null;
  private ctx: AudioContext | null = null;
  private origem: MediaStreamAudioSourceNode | null = null;
  private filtroGrave: BiquadFilterNode | null = null;
  private filtroAgudo: BiquadFilterNode | null = null;
  private analyser: AnalyserNode | null = null;
  private worklet: AudioWorkletNode | null = null;
  private timer: ReturnType<typeof setInterval> | null = null;

  /** Régua temporal + armazenamento do PCM. Trocada a cada montagem do grafo. */
  private linha = new AudioTimeline(new AudioRingBuffer(1), TAXA_PADRAO);
  private vad = new VadDetector({ ...VAD_CONFIG_PADRAO });
  private iaFalando = false;

  /** Reservas vivas do microfone (ver {@link CapturaDono}). */
  private readonly reservas = new Set<CapturaDono>();
  /** Callbacks de evento por dono — só quem se interessa pelo VAD registra um. */
  private readonly ouvintes = new Map<CapturaDono, (e: VadEvento) => void>();
  /** Abertura em andamento, memorizada para que chamadas concorrentes não montem dois grafos. */
  private aberturaEmCurso: Promise<boolean> | null = null;

  readonly status = signal<CaptureStatus>('desligado');
  readonly erro = signal<string>('');
  readonly nivel = signal<number>(0);
  /** Piso de ruído adaptativo do VAD (mesma escala 0..1 de `nivel`). É a base de TODOS os
   *  limiares do detector — expor este valor é o que torna o diagnóstico técnico útil: sem
   *  ele não dá para saber se o VAD deixou de disparar por sala silenciosa (piso baixo, tudo
   *  normal) ou por piso poluído (ruído de fundo alto elevou o limiar). */
  readonly noiseFloor = signal<number>(0);
  /** Limiar de disparo lido DO PRÓPRIO VadDetector (fonte única da fórmula). */
  readonly limiarInicio = signal<number>(0);
  readonly dispositivos = signal<MediaDeviceInfo[]>([]);
  readonly dispositivoSelecionado = signal<string>('');

  get taxaAmostragem(): number {
    return this.linha.taxaHz;
  }

  get ativo(): boolean {
    return this.status() === 'ativo';
  }

  /** Quem detém o microfone neste instante. Consumido pelo VoiceHotwordService para não fingir
   *  sucesso ao trocar de dispositivo durante push-to-talk; a UI de overlay não lê este campo
   *  diretamente — ela confia no `status`/`conversaAtiva` derivados. */
  reservadoPor(dono: CapturaDono): boolean {
    return this.reservas.has(dono);
  }

  constructor() {
    if (isPlatformBrowser(this.platformId)) {
      try {
        this.dispositivoSelecionado.set(localStorage.getItem(STORAGE_KEY) ?? '');
      } catch {
        /* storage indisponível — segue com o dispositivo padrão */
      }
    }
  }

  suportado(): boolean {
    return (
      isPlatformBrowser(this.platformId) &&
      !!navigator?.mediaDevices?.getUserMedia &&
      typeof AudioWorkletNode !== 'undefined'
    );
  }

  /**
   * Reserva o microfone para `dono` e garante o grafo montado.
   *
   * `aoEvento` é OPCIONAL de propósito: o push-to-talk não quer eventos do VAD e, se passasse
   * um callback, sobrescreveria o do modo conversa — era exatamente assim que um clique no mic
   * matava a conversa em silêncio. Cada dono registra (ou não) o seu próprio callback.
   */
  async abrir(dono: CapturaDono, aoEvento?: (e: VadEvento) => void): Promise<boolean> {
    if (!this.suportado()) {
      this.status.set('sem-suporte');
      this.erro.set('Este navegador não suporta captura de áudio em tempo real.');
      return false;
    }

    // Reserva ANTES de qualquer await: o dono já conta mesmo que outro dono esteja montando o
    // grafo neste instante — senão o `fechar` do primeiro derrubaria o microfone do segundo.
    this.reservas.add(dono);
    if (aoEvento) this.ouvintes.set(dono, aoEvento);

    if (this.ativo) return true;

    // Reentrância (duplo clique): devolve a MESMA promise. Sem isto, o segundo `abrir` faria um
    // segundo getUserMedia e sobrescreveria stream/ctx/worklet/timer — o primeiro setInterval e
    // a primeira MediaStream ficavam órfãos, inalcançáveis por `fechar`, com o microfone aberto
    // para sempre.
    this.aberturaEmCurso ??= this.abrirGrafo();
    const ok = await this.aberturaEmCurso;
    if (!ok) {
      this.reservas.delete(dono);
      this.ouvintes.delete(dono);
    }
    return ok;
  }

  /**
   * Devolve a reserva de `dono`. O grafo só cai quando a última reserva sai — o push-to-talk
   * libera o que reservou sem derrubar o modo conversa de quem estava usando antes.
   */
  fechar(dono: CapturaDono): void {
    this.reservas.delete(dono);
    this.ouvintes.delete(dono);
    if (this.reservas.size > 0) {
      // O microfone continua com outro dono. O VAD, porém, reinicia: se o push-to-talk saiu no
      // meio de uma fala, o modo conversa não deve herdar um estado "falando" que não é dele.
      this.vad.reiniciar();
      return;
    }
    this.derrubar();
  }

  definirIaFalando(v: boolean): void {
    this.iaFalando = v;
  }

  /** Extrai a janela pedida do ring buffer, já aplicando o pre-roll, e devolve WAV 16 kHz. */
  async extrairWav(inicioMs: number, fimMs: number): Promise<Blob> {
    const pcm = this.linha.extrair(inicioMs, fimMs, PRE_ROLL_MS);
    return float32ToWav16k(pcm, this.linha.taxaHz);
  }

  /**
   * Push-to-talk: marca o início e devolve um `parar()` que fecha a janela.
   * Usa o MESMO stream e o MESMO buffer do modo conversa — nada de segundo getUserMedia — e a
   * MESMA régua (posição do buffer) que o modo conversa usa para decidir.
   */
  capturaManual(): { parar: () => Promise<Blob> } {
    const inicioMs = this.linha.agoraMs;
    return {
      parar: async () => this.extrairWav(inicioMs, this.linha.agoraMs),
    };
  }

  async listarDispositivos(): Promise<MediaDeviceInfo[]> {
    if (!isPlatformBrowser(this.platformId)) return [];
    try {
      const todos = await navigator.mediaDevices.enumerateDevices();
      const mics = todos.filter(d => d.kind === 'audioinput');
      this.dispositivos.set(mics);
      return mics;
    } catch {
      return [];
    }
  }

  definirDispositivo(id: string): void {
    this.dispositivoSelecionado.set(id);
    try {
      localStorage.setItem(STORAGE_KEY, id);
    } catch {
      /* storage indisponível — a escolha vale só nesta sessão */
    }
  }

  // ── interno ────────────────────────────────────────────────────────────────

  private async abrirGrafo(): Promise<boolean> {
    try {
      this.stream = await navigator.mediaDevices.getUserMedia({ audio: this.constraints() });
      await this.montarGrafo();
      // Todos os donos desistiram durante o await (um `fechar` chegou no meio): derruba agora,
      // em vez de deixar stream e timer vivos sem ninguém para fechá-los.
      if (this.reservas.size === 0) {
        this.derrubar();
        return false;
      }
      await this.listarDispositivos();
      this.status.set('ativo');
      this.erro.set('');
      return true;
    } catch (e: unknown) {
      this.tratarFalha(e);
      this.derrubar();
      return false;
    } finally {
      this.aberturaEmCurso = null;
    }
  }

  /** Desmonta o grafo e devolve o microfone ao sistema. Não decide nada sobre donos. */
  private derrubar(): void {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
    try {
      this.worklet?.port.close();
      this.worklet?.disconnect();
    } catch {
      /* nó já desconectado */
    }
    for (const no of [this.analyser, this.filtroAgudo, this.filtroGrave, this.origem]) {
      try {
        no?.disconnect();
      } catch {
        /* nó já desconectado */
      }
    }
    try {
      void this.ctx?.close();
    } catch {
      /* contexto já fechado */
    }
    this.stream?.getTracks().forEach(t => t.stop());

    this.worklet = null;
    this.analyser = null;
    this.filtroAgudo = null;
    this.filtroGrave = null;
    this.origem = null;
    this.ctx = null;
    this.stream = null;
    // Solta o ring buffer de ~1,3 MB (BUFFER_SEGUNDOS × taxa de amostras Float32) junto com o
    // grafo — sem isto ele ficava retido até o PRÓXIMO montarGrafo() sobrescrever a referência.
    // Placebo de tamanho 1, não null: extrairWav/capturaManual continuam funcionando sem guarda
    // extra (janela vazia/quase-vazia em vez de exceção) se algum `await` pendente os chamar
    // depois deste ponto — hoje nenhum caminho o faz (extração sempre roda ANTES de `fechar`).
    this.linha = new AudioTimeline(new AudioRingBuffer(1), TAXA_PADRAO);
    this.reservas.clear();
    this.ouvintes.clear();
    this.iaFalando = false;
    this.vad.reiniciar();
    this.nivel.set(0);
    this.noiseFloor.set(0);
    this.limiarInicio.set(0);
    if (this.status() === 'ativo') this.status.set('desligado');
  }

  private constraints(): MediaTrackConstraints {
    const id = this.dispositivoSelecionado();
    return {
      ...(id ? { deviceId: { exact: id } } : {}),
      // AGC amplifica o silêncio até o ruído cruzar o limiar — é o "dispara sozinho". Fora.
      autoGainControl: false,
      // echoCancellation não é preferência: é o que impede o TTS de voltar pelo microfone
      // e disparar barge-in sozinho.
      echoCancellation: true,
      noiseSuppression: true,
      channelCount: 1,
    };
  }

  private async montarGrafo(): Promise<void> {
    const Ctx: typeof AudioContext =
      (window as unknown as { AudioContext: typeof AudioContext }).AudioContext ||
      (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;

    // Pedir 16 kHz na origem dispensa resample depois. Browsers que recusarem entregam a taxa
    // nativa e o float32ToWav16k resampleia — por isso a taxa nunca é assumida, sempre lida.
    try {
      this.ctx = new Ctx({ sampleRate: 16000 });
    } catch {
      this.ctx = new Ctx();
    }
    if (this.ctx.state === 'suspended') await this.ctx.resume();

    const taxa = this.ctx.sampleRate;
    // Régua nova a cada grafo: a origem do tempo é a PRIMEIRA amostra escrita pelo worklet,
    // não a criação do contexto (que já começou a contar antes do addModule abaixo).
    this.linha = new AudioTimeline(new AudioRingBuffer(Math.round(taxa * BUFFER_SEGUNDOS)), taxa);
    this.vad = new VadDetector({ ...VAD_CONFIG_PADRAO });

    this.origem = this.ctx.createMediaStreamSource(this.stream!);

    // Ramo de DECISÃO: passa-altas em 300 Hz + passa-baixas em 3400 Hz ≈ banda de voz.
    // Corta ar-condicionado e motor, que inflavam o RMS de espectro cheio.
    this.filtroGrave = this.ctx.createBiquadFilter();
    this.filtroGrave.type = 'highpass';
    this.filtroGrave.frequency.value = 300;
    this.filtroAgudo = this.ctx.createBiquadFilter();
    this.filtroAgudo.type = 'lowpass';
    this.filtroAgudo.frequency.value = 3400;
    this.analyser = this.ctx.createAnalyser();
    this.analyser.fftSize = 512;
    this.origem.connect(this.filtroGrave);
    this.filtroGrave.connect(this.filtroAgudo);
    this.filtroAgudo.connect(this.analyser);

    // Ramo de GRAVAÇÃO: direto da origem, SEM filtro.
    await this.ctx.audioWorklet.addModule('assets/audio/pcm-recorder.worklet.js');
    this.worklet = new AudioWorkletNode(this.ctx, 'pcm-recorder');
    this.worklet.port.onmessage = (ev: MessageEvent<Float32Array>) => this.linha.escrever(ev.data);
    this.origem.connect(this.worklet);

    this.iniciarLacoDecisao();
  }

  private iniciarLacoDecisao(): void {
    const buf = new Uint8Array(this.analyser!.fftSize);
    this.timer = setInterval(() => {
      const analyser = this.analyser;
      if (!analyser) return;
      analyser.getByteTimeDomainData(buf);

      let soma = 0;
      for (let i = 0; i < buf.length; i++) {
        const v = (buf[i] - 128) / 128;
        soma += v * v;
      }
      const nivel = Math.min(1, Math.sqrt(soma / buf.length) * 3);
      this.nivel.set(nivel);

      // Régua do BUFFER (posição escrita), não currentTime: a mesma que `extrairWav` usa para
      // converter ms→amostra. É o que garante que o pre-roll de 300 ms exista de verdade,
      // independente de T0, de atraso de fila ou de quadros perdidos.
      const eventos = this.vad.processar(nivel, this.linha.agoraMs, this.iaFalando);
      // Lidos DEPOIS de processar(): são o `floor` e o limiar já atualizados para este quadro.
      this.noiseFloor.set(this.vad.noiseFloor);
      this.limiarInicio.set(this.vad.limiarInicio);
      for (const e of eventos) this.despachar(e);
    }, PASSO_DECISAO_MS);
  }

  /**
   * Entrega o evento aos donos interessados.
   *
   * Enquanto o push-to-talk detém o microfone, o VAD não fala com ninguém: o usuário já marcou
   * início e fim da fala com o botão, e um envio automático em paralelo duplicaria a pergunta.
   * (Antes desta correção o mesmo efeito acontecia por acidente — o `abrir` do manual trocava o
   * callback do modo conversa por um no-op — só que sem volta: a conversa morria em silêncio.)
   */
  private despachar(e: VadEvento): void {
    if (this.reservas.has('manual')) return;
    for (const cb of this.ouvintes.values()) cb(e);
  }

  private tratarFalha(e: unknown): void {
    const nome = (e as { name?: string })?.name ?? '';
    const msg = (e as { message?: string })?.message ?? nome ?? 'erro';
    if (nome === 'NotAllowedError' || nome === 'SecurityError') {
      this.status.set('permissao-negada');
      this.erro.set('Permissão de microfone negada. Libere o microfone do site e tente novamente.');
    } else if (nome === 'NotFoundError' || nome === 'OverconstrainedError') {
      this.status.set('erro');
      this.erro.set('O microfone selecionado não foi encontrado. Escolha outro dispositivo.');
    } else {
      this.status.set('erro');
      this.erro.set('Não consegui acessar o microfone: ' + msg + '.');
    }
  }
}
